import { c as _c } from "react/compiler-runtime";
import { BulkExportMembers } from "@components/table/members/bulk-actions/bulk-export-members";
import { BulkRemoveMemberDropdown } from "@components/table/members/bulk-actions/bulk-remove-member-dropdown";
import { buildAvailableRemoveOptions } from "@components/table/members/bulk-actions/bulk-remove-options-utils";
import { BulkResendInvitation } from "@components/table/members/bulk-actions/bulk-resend-invitation";
import { BulkSendEmail } from "@components/table/members/bulk-actions/bulk-send-email";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useTeamsAccess } from "@hooks/access-control/use-teams-access";
import { useShouldShowBulkAction } from "@hooks/table/use-should-show-bulk-action";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { fetchingTeamsFlagsSelector } from "@store/teams/teams-selector";
import { useMemo } from "react";
/** List all the bulk action components for team members table */
export function useTeamMembersPageBulkActions(t0) {
  const $ = _c(42);
  const {
    companyId,
    team,
    teamMembers
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = selectedEntitiesSelector("members");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const selectedEntities = useAppSelector(t1);
  const {
    isFetchingTeamMembers
  } = useAppSelector(fetchingTeamsFlagsSelector);
  const isFetching = isFetchingTeamMembers === "pending";
  const isAnyRowSelected = selectedEntities.length !== 0;
  let t2;
  if ($[1] !== selectedEntities) {
    t2 = selectedEntities.some(_temp);
    $[1] = selectedEntities;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isAnyMemberPending = t2;
  const {
    canDeleteMemberFromCompany,
    canResendInvitationToCompany
  } = useHasUserValidRoleCompanyLevel();
  const {
    canDeleteMembersFromTeam
  } = useTeamsAccess();
  const canRemoveFromTeam = canDeleteMembersFromTeam && !team.category.isManaged;
  const canRemoveFromWorkspace = canDeleteMemberFromCompany;
  let allowedButtons;
  if ($[3] !== canRemoveFromTeam || $[4] !== canRemoveFromWorkspace || $[5] !== canResendInvitationToCompany || $[6] !== isAnyMemberPending || $[7] !== isAnyRowSelected) {
    allowedButtons = ["export"];
    if (isAnyRowSelected) {
      allowedButtons.push("sendEmail");
      if (isAnyMemberPending && canResendInvitationToCompany) {
        allowedButtons.push("resendInvitation");
      }
      if (canRemoveFromTeam || canRemoveFromWorkspace) {
        allowedButtons.push("removeMember");
      }
    }
    $[3] = canRemoveFromTeam;
    $[4] = canRemoveFromWorkspace;
    $[5] = canResendInvitationToCompany;
    $[6] = isAnyMemberPending;
    $[7] = isAnyRowSelected;
    $[8] = allowedButtons;
  } else {
    allowedButtons = $[8];
  }
  const allowedButtonsBasedOnRowSelection = allowedButtons;
  let t3;
  if ($[9] !== allowedButtonsBasedOnRowSelection) {
    t3 = {
      allowedButtonsBasedOnRowSelection
    };
    $[9] = allowedButtonsBasedOnRowSelection;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const {
    shouldShowBulkButtons
  } = useShouldShowBulkAction(t3);
  let t4;
  if ($[11] !== isFetching || $[12] !== shouldShowBulkButtons || $[13] !== teamMembers) {
    t4 = shouldShowBulkButtons("export") && (isFetching || teamMembers.length > 0);
    $[11] = isFetching;
    $[12] = shouldShowBulkButtons;
    $[13] = teamMembers;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const shouldShowExportButton = t4;
  let t5;
  if ($[15] !== canRemoveFromTeam || $[16] !== canRemoveFromWorkspace) {
    t5 = buildAvailableRemoveOptions({
      canRemoveFromTeam,
      canRemoveFromWorkspace
    });
    $[15] = canRemoveFromTeam;
    $[16] = canRemoveFromWorkspace;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const availableRemoveOptions = t5;
  let t6;
  if ($[18] !== companyId || $[19] !== selectedEntities || $[20] !== shouldShowBulkButtons) {
    t6 = shouldShowBulkButtons("resendInvitation") ? <BulkResendInvitation selectedEntities={selectedEntities} companyId={companyId} subjectType="team" /> : null;
    $[18] = companyId;
    $[19] = selectedEntities;
    $[20] = shouldShowBulkButtons;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== availableRemoveOptions || $[23] !== companyId || $[24] !== shouldShowBulkButtons || $[25] !== team) {
    t7 = shouldShowBulkButtons("removeMember") && availableRemoveOptions.length > 0 ? <BulkRemoveMemberDropdown companyId={companyId} teamId={team.id} availableOptions={availableRemoveOptions} /> : null;
    $[22] = availableRemoveOptions;
    $[23] = companyId;
    $[24] = shouldShowBulkButtons;
    $[25] = team;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== selectedEntities || $[28] !== shouldShowBulkButtons) {
    t8 = shouldShowBulkButtons("sendEmail") ? <BulkSendEmail selectedEntities={selectedEntities} /> : null;
    $[27] = selectedEntities;
    $[28] = shouldShowBulkButtons;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== companyId || $[31] !== isAnyRowSelected || $[32] !== selectedEntities || $[33] !== shouldShowExportButton || $[34] !== team || $[35] !== teamMembers) {
    t9 = shouldShowExportButton ? <BulkExportMembers selectedEntities={isAnyRowSelected ? selectedEntities : teamMembers} subjectType="team" entityName={team.name} areEntitiesSelected={isAnyRowSelected} teamId={team.id} companyId={companyId} /> : null;
    $[30] = companyId;
    $[31] = isAnyRowSelected;
    $[32] = selectedEntities;
    $[33] = shouldShowExportButton;
    $[34] = team;
    $[35] = teamMembers;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== t6 || $[38] !== t7 || $[39] !== t8 || $[40] !== t9) {
    t10 = {
      resendInvitation: t6,
      removeMember: t7,
      sendEmail: t8,
      export: t9
    };
    $[37] = t6;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  return t10;
}
function _temp(member) {
  return !FaroCoreApiUtils.Member.isMemberActive(member);
}